
import { useEffect, useState } from "react";
import { useTranslations } from "use-intl";
import { Sun, Moon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/utils/cn";

const STORAGE_KEY = "examora-theme";
type Theme = "light" | "dark";

function readStoredTheme(): Theme | null {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === "light" || value === "dark" ? value : null;
  } catch {
    return null;
  }
}

/**
 * The product default is light (index.html's inline script sets
 * data-theme="light" unless the user stored "dark"), with this explicit manual
 * override on top — see the `[data-theme]` blocks in globals.css and the
 * blocking inline script in the root layout (which sets the attribute
 * before first paint, so there's no flash of the wrong theme). This
 * component only handles the *interactive* half: reading back whatever
 * the inline script already resolved, and flipping it on click.
 */
export function ThemeToggle({ className }: { className?: string }) {
  const t = useTranslations("common.theme");
  // null until mounted — we don't know the real (possibly OS-driven)
  // theme during SSR, and guessing wrong would flash the wrong icon.
  const [theme, setTheme] = useState<Theme | null>(null);

  useEffect(() => {
    const current = (document.documentElement.getAttribute("data-theme") as Theme | null) ?? readStoredTheme();
    // eslint-disable-next-line react-hooks/set-state-in-effect -- resolving the client-only current theme (attribute set by the pre-hydration inline script) on mount
    setTheme(current ?? "light");
  }, []);

  function toggle() {
    const next: Theme = theme === "light" ? "dark" : "light";
    setTheme(next);
    document.documentElement.setAttribute("data-theme", next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Private browsing / storage disabled — the toggle still works for
      // this page load via the attribute, it just won't persist.
    }
  }

  if (theme === null) {
    return <div className={cn("h-9 w-9", className)} aria-hidden="true" />;
  }

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      onClick={toggle}
      aria-label={theme === "light" ? t("switchToDark") : t("switchToLight")}
      className={className}
    >
      {theme === "light" ? <Moon className="h-4 w-4" aria-hidden="true" /> : <Sun className="h-4 w-4" aria-hidden="true" />}
    </Button>
  );
}
